"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, BookOpen, Calendar, Clock, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
const copy = {
  badge: {
    en: "Engineering & Filtration Library",
    zh: "工程与过滤技术文库",
    es: "Biblioteca de ingeniería y filtración",
    de: "Engineering- & Filtrationsbibliothek",
    fr: "Bibliothèque d'ingénierie et de filtration",
    pt: "Biblioteca de engenharia e filtração"
  },
  title: {
    en: "Technical insights and mesh engineering guides",
    zh: "技术解析与丝网工程指南",
    es: "Conocimientos técnicos y guías de ingeniería de mallas",
    de: "Technische Einblicke und Drahtgewebe-Engineering-Leitfäden",
    fr: "Analyses techniques et guides d'ingénierie des toiles",
    pt: "Artigos técnicos e guias de engenharia de malhas"
  },
  subtitle: {
    en: "Explore practical filtration calculations, material standards, and weaving specifications for your industrial requirements.",
    zh: "探索符合工业应用标准的过滤计算、材料规范与织造工艺技术指南。",
    es: "Explore cálculos prácticos de filtración, normas de materiales y especificaciones de tejido para sus requisitos industriales.",
    de: "Praxisnahe Filtrationsberechnungen, Werkstoffnormen und Webspezifikationen für Ihre industriellen Anforderungen.",
    fr: "Découvrez des calculs pratiques de filtration, des normes de matériaux et des spécifications de tissage adaptées à vos besoins industriels.",
    pt: "Explore cálculos práticos de filtração, normas de materiais e especificações de tecelagem para suas necessidades industriais."
  },
  viewAll: {
    en: "View all resources",
    zh: "查看全部资源",
    es: "Ver todos los recursos",
    de: "Alle Ressourcen ansehen",
    fr: "Voir toutes les ressources",
    pt: "Ver todos os recursos"
  },
  errorTitle: {
    en: "Unable to load technical articles",
    zh: "无法加载技术文章",
    es: "No se pudieron cargar los artículos técnicos",
    de: "Fachartikel konnten nicht geladen werden",
    fr: "Impossible de charger les articles techniques",
    pt: "Não foi possível carregar os artigos técnicos"
  },
  errorSubtitle: {
    en: "Please check your network connection and try again.",
    zh: "请检查网络连接后重试。",
    es: "Por favor verifique su conexión e intente nuevamente.",
    de: "Bitte prüfen Sie Ihre Netzwerkverbindung und versuchen Sie es erneut.",
    fr: "Veuillez vérifier votre connexion réseau et réessayer.",
    pt: "Verifique sua conexão de rede e tente novamente."
  },
  retry: {
    en: "Retry loading",
    zh: "重新加载",
    es: "Reintentar",
    de: "Erneut versuchen",
    fr: "Réessayer",
    pt: "Tentar novamente"
  },
  emptyTitle: {
    en: "No articles available",
    zh: "暂无可用文章",
    es: "No hay artículos disponibles",
    de: "Keine Artikel verfügbar",
    fr: "Aucun article disponible",
    pt: "Nenhum artigo disponível"
  },
  emptySubtitle: {
    en: "Check back soon for new technical publications and wire mesh guidelines.",
    zh: "我们即将发布更多技术文献与丝网工程指南，敬请期待。",
    es: "Consulte próximamente nuevas publicaciones técnicas y guías de mallas metálicas.",
    de: "In Kürze finden Sie hier neue Fachbeiträge und Leitfäden zu Drahtgeweben.",
    fr: "Revenez bientôt pour de nouvelles publications techniques et guides sur les toiles métalliques.",
    pt: "Volte em breve para conferir novas publicações técnicas e guias sobre telas metálicas."
  },
  browseResources: {
    en: "Browse technical resources",
    zh: "浏览技术资源",
    es: "Explorar recursos técnicos",
    de: "Technische Ressourcen ansehen",
    fr: "Consulter les ressources techniques",
    pt: "Explorar recursos técnicos"
  },
  defaultCategory: {
    en: "Technical Guide",
    zh: "技术指南",
    es: "Guía técnica",
    de: "Technischer Leitfaden",
    fr: "Guide technique",
    pt: "Guia técnico"
  },
  readArticle: {
    en: "Read technical article",
    zh: "阅读技术文章",
    es: "Leer artículo técnico",
    de: "Fachartikel lesen",
    fr: "Lire l'article technique",
    pt: "Ler artigo técnico"
  },
  pageStatus: (current: number, total: number): Record<Lang, string> => ({
    en: `Showing page ${current} of ${total}`,
    zh: `第 ${current} 页，共 ${total} 页`,
    es: `Página ${current} de ${total}`,
    de: `Seite ${current} von ${total}`,
    fr: `Page ${current} sur ${total}`,
    pt: `Página ${current} de ${total}`
  })
};
const dateLocales: Record<Lang, string> = {
  en: "en-US",
  zh: "zh-CN",
  es: "es-ES",
  de: "de-DE",
  fr: "fr-FR",
  pt: "pt-BR"
};
function ArticlesConsumer() {
  const {
    lang
  } = useLanguage();
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {Array.from({
        length: 6
      }).map((_, index) => <div key={index} data-index={index} className="flex flex-col gap-4 p-6 rounded-lg border border-border bg-card">
            <Skeleton className="h-48 w-full rounded-md" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-20 rounded-md" />
              <Skeleton className="h-4 w-24 rounded-md" />
            </div>
            <Skeleton className="h-6 w-4/5 rounded-md" />
            <Skeleton className="h-4 w-full rounded-md" />
            <Skeleton className="h-4 w-3/4 rounded-md" />
            <Skeleton className="h-9 w-28 rounded-md mt-2" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="p-8 text-center rounded-lg border border-border bg-card max-w-xl mx-auto">
        <FileText className="size-10 mx-auto text-muted-foreground mb-3" aria-hidden="true" />
        <p className="text-base font-medium text-foreground mb-1">
          {pick(lang, copy.errorTitle)}
        </p>
        <p className="mf-caption text-muted-foreground mb-4">
          {pick(lang, copy.errorSubtitle)}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {pick(lang, copy.retry)}
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="p-10 text-center rounded-lg border border-border bg-card max-w-lg mx-auto">
        <BookOpen className="size-10 mx-auto text-muted-foreground mb-3" aria-hidden="true" />
        <p className="text-base font-semibold text-foreground">
          {pick(lang, copy.emptyTitle)}
        </p>
        <p className="mf-caption text-muted-foreground mt-1 mb-6">
          {pick(lang, copy.emptySubtitle)}
        </p>
        <Button asChild variant="outline">
          <Link to="/TechnicalResourcesPage">
            {pick(lang, copy.browseResources)}
          </Link>
        </Button>
      </div>;
  }
  return <div className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {posts.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : pick(lang, copy.defaultCategory);
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString(dateLocales[lang] || "en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        }) : null;
        return <Reveal key={post.id} delay={Math.min(i * 60, 300)}>
              <Card data-index={i} className="flex flex-col justify-between border-border bg-card transition-[border-color] duration-200 hover:border-primary/40 overflow-hidden h-full">
                <div>
                  {post.featuredImage ? <div className="mf-card-media relative mf-ratio-card w-full">
                      <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div> : <div className="mf-card-media mf-ratio-card w-full bg-muted/60 flex items-center justify-center text-muted-foreground">
                      <FileText className="size-10 stroke-[1.5]" aria-hidden="true" />
                    </div>}

                  <CardHeader className="space-y-3 pt-5">
                    <div className="flex flex-wrap items-center justify-between gap-2 mf-caption text-muted-foreground">
                      <Badge variant="outline" className="font-normal mf-caption px-2.5 py-0.5 bg-muted text-foreground border border-border">
                        {categoryName}
                      </Badge>
                      {formattedDate && <span className="inline-flex items-center gap-1.5">
                          <SafeHydrate><Calendar className="size-3.5" aria-hidden="true" /></SafeHydrate>
                          {formattedDate}
                        </span>}
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground leading-snug line-clamp-2">
                      {post.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="pt-0">
                    {post.excerpt && <p className="mf-caption text-muted-foreground line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{
                  __html: post.excerpt.replace(/<[^>]*>?/gm, "")
                }} />}
                  </CardContent>
                </div>

                <CardFooter className="pt-4 border-t border-border mt-auto">
                  <Button asChild variant="ghost" size="sm" className="p-0 h-auto font-medium text-primary hover:text-primary/80 hover:bg-transparent">
                    <Link to={post.link || "#"} className="inline-flex items-center gap-1.5">
                      <span>{pick(lang, copy.readArticle)}</span>
                      <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </Reveal>;
      })}
      </div>

      {(hasNext || hasPrev) && <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
          <p className="mf-caption text-muted-foreground">
            {pick(lang, copy.pageStatus(currentPage, totalPages))}
          </p>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev}>
              {pick(lang, COMMON.previous)}
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext}>
              {pick(lang, COMMON.next)}
            </Button>
          </div>
        </div>}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  return <section id="relatedarticles" data-nav="light" className="bg-background text-foreground mf-section border-t border-border">
      <div className="mf-container">
        <Reveal delay={0}>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mf-caption font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                <Clock className="size-4 text-primary" aria-hidden="true" />
                <span>{pick(lang, copy.badge)}</span>
              </div>
              <h2 className="mf-h2 font-semibold text-foreground">
                {pick(lang, copy.title)}
              </h2>
              <p className="text-base text-muted-foreground mt-3 leading-relaxed">
                {pick(lang, copy.subtitle)}
              </p>
            </div>

            <div className="shrink-0">
              <Button asChild variant="outline">
                <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, copy.viewAll)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }} className="posts-container">
          <ArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}